import React, { useState, useContext, useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { LoruleRequestConfig } from '@galaxy/lorule-react-commons';
import { Modal, Button } from 'react-bootstrap';
import { ResponseError, SubmitButton } from '@nintendo/forms';
import { Column } from 'react-table';
import { RouteComponentProps } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import { INITIAL_SELECT_ID, COMMENT_MAX_LENGTH } from '../../../utils/Constants';
import NinModal from '../../common/modal/NinModal';
import { sendToast, formatAmDisplayString } from '../../../utils/displayUtils';
import {
    holdActionMemo,
    releaseActionMemo,
    approveActionMemo,
    returnActionMemo,
    skipActionMemo,
} from '../../../api/actionMemoWorkflowApi';
import DataTable from '../../common/datatables/DataTable';
import { Comment } from '../../../api/actionMemoApiTypes';
import TextCountedField from '../../common/TextCountedField';

export enum AmWorkflowFormType {
    APPROVE = 'APPROVE',
    RETURN = 'RETURN',
    SKIP = 'SKIP',
    HOLD = 'HOLD',
    RELEASE = 'RELEASE',
}

export interface AmWorkflowFormBaseProps {
    title: String;
    toggleButton: JSX.Element;
    formType: AmWorkflowFormType;
    onSubmit?: Function;
}

export interface AmWorkflowModalProps extends AmWorkflowFormBaseProps {
    amId: number;
    amNumber: number;
    supNumber: number;
    comments: Comment[];
    content?: JSX.Element | null;
    routerProps?: RouteComponentProps;
}

const submitAmWorkflow = async (
    config: LoruleRequestConfig,
    formType: AmWorkflowFormType,
    values: {
        amId: number;
        amNumber: number;
        supNumber: number;
        commentMessage: string;
    },
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
    routerProps?: RouteComponentProps,
) => {
    try {
        if (formType === AmWorkflowFormType.APPROVE) {
            await approveActionMemo(config, values.amId, values.commentMessage);
            sendToast(
                <>
                    Approved Action Memo:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmWorkflowFormType.RETURN) {
            await returnActionMemo(config, values.amId, values.commentMessage);
            sendToast(
                <>
                    Returned Action Memo:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmWorkflowFormType.SKIP) {
            await skipActionMemo(config, values.amId, values.commentMessage);
            sendToast(
                <>
                    Skipped Action Memo:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmWorkflowFormType.HOLD) {
            await holdActionMemo(config, values.amId, values.commentMessage);
            sendToast(
                <>
                    Action Memo On Hold:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmWorkflowFormType.RELEASE) {
            await releaseActionMemo(config, values.amId, values.commentMessage);
            sendToast(
                <>
                    Released Action Memo:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        }
        if (routerProps) {
            routerProps.history.push(`/tasks`);
        }

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(`Workflow failed:  ${error.message}`);
        }
        setSubmitting(false);
        return false;
    }
};

const AmWorkflowModal = (props: AmWorkflowModalProps) => {
    const config = useContext(ConfigContext);

    const [responseError, setResponseError] = useState('');

    const amWorkflowValidationSchema = Yup.object().shape({
        commentMessage: Yup.string()
            // eslint-disable-next-line no-template-curly-in-string
            .max(COMMENT_MAX_LENGTH, 'Comment exceeds max length of ${max} characters')
            .test(
                'required-check',
                'Comment is required',
                (commentMessage) =>
                    !!(props.formType !== AmWorkflowFormType.RETURN || commentMessage),
            ),
    });

    const initialValues = {
        amId: props.amId ? props.amId : INITIAL_SELECT_ID,
        amNumber: props.amNumber,
        supNumber: props.supNumber,
        commentMessage: '',
    };

    const columns = React.useMemo(
        () => [
            {
                Header: 'Name/Title',
                accessor: 'commentedByName',
                disableSortBy: true,
                Cell: (cellProps: {
                    value: string;
                    row: {
                        original: {
                            commentedByTitle: string;
                        };
                    };
                }) =>
                    useMemo(
                        () => (
                            <>
                                <div>{cellProps.value}</div>
                                <div className="font-italic font-weight-light">
                                    <small>{cellProps.row.original.commentedByTitle}</small>
                                </div>
                            </>
                        ),
                        [],
                    ),
            },
            {
                Header: 'Comment',
                accessor: 'commentMessage',
                disableSortBy: true,
            },
        ],
        [],
    );

    let actionText = 'Submit';
    switch (props.formType) {
        case AmWorkflowFormType.APPROVE:
            actionText = 'Approve';
            break;
        case AmWorkflowFormType.RETURN:
            actionText = 'Return';
            break;
        case AmWorkflowFormType.SKIP:
            actionText = 'Skip';
            break;
        case AmWorkflowFormType.HOLD:
            actionText = 'Hold';
            break;
        case AmWorkflowFormType.RELEASE:
            actionText = 'Release';
            break;
        default:
            break;
    }

    return (
        <NinModal
            button={props.toggleButton}
            size="lg"
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={amWorkflowValidationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await submitAmWorkflow(
                            config,
                            props.formType,
                            values,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                            props.routerProps,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={({ values, touched, errors, isSubmitting }) => (
                        <>
                            <Modal.Header closeButton>
                                <Modal.Title>{actionText} Action Memo</Modal.Title>
                            </Modal.Header>
                            <Form>
                                <Modal.Body>
                                    <ResponseError errorMessage={responseError} />

                                    {props.content}

                                    <TextCountedField
                                        label={`Comment${
                                            props.formType !== AmWorkflowFormType.RETURN
                                                ? ' (Optional)'
                                                : ''
                                        }`}
                                        name="commentMessage"
                                        touched={touched.commentMessage}
                                        errors={errors.commentMessage}
                                        isRequired={props.formType === AmWorkflowFormType.RETURN}
                                        testId="commentMessage"
                                        length={
                                            values.commentMessage ? values.commentMessage.length : 0
                                        }
                                        maxLength={COMMENT_MAX_LENGTH}
                                    />
                                    {props.comments && props.comments.length > 0 && (
                                        <div className="react-table px-2 table-scroll height-200">
                                            <DataTable
                                                columns={columns as Column<Object>[]}
                                                data={props.comments}
                                                noGlobalFilter
                                                noPagination
                                                initialState={{ pageSize: 1000 }}
                                            />
                                        </div>
                                    )}
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={toggleModal}
                                        disabled={isSubmitting}
                                    >
                                        Close
                                    </Button>
                                    <SubmitButton
                                        className="btn-primary ml-auto"
                                        disabled={
                                            props.formType === AmWorkflowFormType.RETURN &&
                                            !values.commentMessage
                                        }
                                        isSubmitting={isSubmitting}
                                        buttonText={actionText}
                                    />
                                </Modal.Footer>
                            </Form>
                        </>
                    )}
                />
            )}
        />
    );
};

export default AmWorkflowModal;
